import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  CircularProgress,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import BiweeklyReportTable from './BiweeklyReportTable';
import BiweeklyReportModal from './BiweeklyReportModal';
import { BIWEEKLY_REPORT_LIST_UI } from './constants';
import apiClient from '../../requests/api/apiClient';
import { FetchError } from '../../views/types';
import { ReduxState } from '../../types';
import { UserRole, UserType } from '../../constants/user_types';

interface BiweeklyReport {
  id: string;
  startDate: string;
  endDate: string;
  createdBy: string;
  createdAt: string;
}

interface BiweeklyReportTabProps {
  sectorId: string;
}

const BiweeklyReportTab: React.FC<BiweeklyReportTabProps> = ({ sectorId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const currentUser = useSelector((state: ReduxState) => state.account.user);
  const [biweeklyReports, setBiweeklyReports] = useState<BiweeklyReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Permissions logic
  const isAdmin = currentUser?.role === UserRole.ADMIN && currentUser?.type === UserType.CDTEC;
  const isAgro = currentUser?.role === UserRole.AGRO && currentUser?.type === UserType.CDTEC;

  const canCreate = isAdmin || isAgro;
  const canDelete = isAdmin || isAgro;

  const loadBiweeklyReports = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.biweeklyReport.biweeklyReportList(sectorId);
      setBiweeklyReports(data.biweeklyReports);
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sectorId) {
      loadBiweeklyReports();
    }
  }, [sectorId]);

  const handleCreateReport = () => {
    setModalOpen(true);
  };

  const handleViewReport = (report: BiweeklyReport) => {
    history.push(`/informe-quincenal/${report.id}`);
  };

  const handleDeleteReport = async (report: BiweeklyReport) => {
    try {
      await apiClient.biweeklyReport.biweeklyReportDelete(report.id);
      enqueueSnackbar('Informe quincenal eliminado exitosamente', { variant: 'success' });
      loadBiweeklyReports();
    } catch (error) {
      enqueueSnackbar('Error al eliminar el informe quincenal', { variant: 'error' });
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleReportSaved = () => {
    loadBiweeklyReports();
    handleModalClose();
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box />
            {canCreate && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateReport}
              >
                Agregar informe Quincenal
              </Button>
            )}
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : (
            <BiweeklyReportTable
              reports={biweeklyReports}
              headers={BIWEEKLY_REPORT_LIST_UI}
              loading={loading}
              onView={handleViewReport}
              onDelete={canDelete ? handleDeleteReport : undefined}
            />
          )}
        </CardContent>
      </Card>

      <BiweeklyReportModal
        open={modalOpen}
        onClose={handleModalClose}
        mode="create"
        reportData={null}
        sectorId={sectorId}
        onReportSaved={handleReportSaved}
      />
    </Box>
  );
};

export default BiweeklyReportTab;
